(function () {
  'use strict';

  class SaTemplates extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerEnd = this.handlePointerEnd.bind(this);
      this.handlePointerCancel = this.handlePointerCancel.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    }

    connectedCallback() {
      if (this.isConnectedToEvents) return;

      this.refreshItems();
      this.activeIndex = Math.max(
        0,
        this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
      );
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      if (this.panelsStage) {
        this.panelsStage.addEventListener('pointerdown', this.handlePointerDown);
        this.panelsStage.addEventListener('pointermove', this.handlePointerMove);
        this.panelsStage.addEventListener('pointerup', this.handlePointerEnd);
        this.panelsStage.addEventListener('pointercancel', this.handlePointerCancel);
        this.panelsStage.addEventListener('transitionend', this.handleTransitionEnd);
      }
      this.updatePanels(this.activeIndex, false);
      this.isConnectedToEvents = true;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      if (this.panelsStage) {
        this.panelsStage.removeEventListener('pointerdown', this.handlePointerDown);
        this.panelsStage.removeEventListener('pointermove', this.handlePointerMove);
        this.panelsStage.removeEventListener('pointerup', this.handlePointerEnd);
        this.panelsStage.removeEventListener('pointercancel', this.handlePointerCancel);
        this.panelsStage.removeEventListener('transitionend', this.handleTransitionEnd);
      }
      if (this.jumpFrame) cancelAnimationFrame(this.jumpFrame);
      this.jumpFrame = null;
      this.isConnectedToEvents = false;
    }

    refreshItems() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.panelsStage = this.querySelector('.sa-templates__panels');
      this.controls = this.querySelector('.sa-templates__controls');
      this.pagination = this.controls?.querySelector('.swiper-pagination') || null;
      this.progressFill = this.pagination?.querySelector('.swiper-pagination-progressbar-fill') || null;

      if (this.pagination && !this.progressFill) {
        this.pagination.classList.add('swiper-pagination-progressbar', 'swiper-pagination-horizontal');
        this.pagination.setAttribute('aria-hidden', 'true');
        this.progressFill = document.createElement('span');
        this.progressFill.className = 'swiper-pagination-progressbar-fill';
        this.pagination.append(this.progressFill);
      }
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;

      const previousButton = event.target.closest('.sa-templates__controls .swiper-button-prev');
      const nextButton = event.target.closest('.sa-templates__controls .swiper-button-next');
      const navigationButton = previousButton || nextButton;
      if (navigationButton && navigationButton.closest('sa-templates') === this) {
        this.refreshItems();
        const direction = nextButton ? 1 : -1;
        this.activate((this.activeIndex + direction + this.tabs.length) % this.tabs.length);
        return;
      }

      const tabsNext = event.target.closest('[data-tabs-next]');
      if (tabsNext && tabsNext.closest('sa-templates') === this) {
        this.refreshItems();
        this.activate((this.activeIndex + 1) % this.tabs.length);
        return;
      }

      const tab = event.target.closest('[role="tab"]');
      if (!tab || tab.closest('sa-templates') !== this) return;

      this.refreshItems();
      this.activate(this.tabs.indexOf(tab));
    }

    handleKeydown(event) {
      if (!(event.target instanceof Element)) return;

      const tab = event.target.closest('[role="tab"]');
      if (!tab || tab.closest('sa-templates') !== this) return;

      this.refreshItems();
      const index = this.tabs.indexOf(tab);
      let nextIndex;

      if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      if (nextIndex === undefined) return;

      event.preventDefault();
      this.activate(nextIndex, true);
    }

    handleBlockSelect(event) {
      if (!(event.target instanceof Element)) return;

      const panel = event.target.closest('[data-block-id]');
      if (!panel || panel.closest('sa-templates') !== this) return;

      this.refreshItems();
      this.activate(this.panels.indexOf(panel));
    }

    activate(index, moveFocus = false) {
      if (index < 0 || !this.tabs[index] || !this.panels[index]) return;

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.updatePanels(index, index !== this.activeIndex);
      this.scrollTabIntoView(this.tabs[index]);
      if (moveFocus) this.tabs[index].focus();
    }

    updatePanels(index, animate) {
      if (!this.panels.length) return;

      if (this.jumpFrame) cancelAnimationFrame(this.jumpFrame);
      const oldActiveIndex = Number.isInteger(this.activeIndex) ? this.activeIndex : index;
      const jumpingPanels = [];

      this.panels.forEach((panel, panelIndex) => {
        const oldPosition = Number.parseInt(panel.dataset.slidePosition, 10) || 0;
        const newPosition = this.getRelativePosition(panelIndex, index);
        const isActive = panelIndex === index;
        const isPrevious = newPosition === -1;
        const isNext = newPosition === 1;
        const crossesWrap =
          animate &&
          Math.abs(newPosition - oldPosition) > 2 &&
          panelIndex !== index &&
          panelIndex !== oldActiveIndex;
        const isMoving =
          animate &&
          !this.reducedMotion.matches &&
          (Math.abs(oldPosition) <= 1 || Math.abs(newPosition) <= 1);

        panel.classList.toggle('is-jumping', crossesWrap);
        panel.classList.toggle('is-moving', isMoving);
        panel.classList.toggle('is-active', isActive);
        panel.classList.toggle('is-previous', isPrevious);
        panel.classList.toggle('is-next', isNext);
        panel.setAttribute('aria-hidden', String(!isActive));
        panel.dataset.slidePosition = String(newPosition);
        if (crossesWrap) jumpingPanels.push(panel);
      });

      this.activeIndex = index;
      this.updateControls();
      if (jumpingPanels.length) {
        this.jumpFrame = requestAnimationFrame(() => {
          jumpingPanels.forEach((panel) => panel.classList.remove('is-jumping'));
          this.jumpFrame = null;
        });
      }
    }

    updateControls() {
      if (!this.progressFill || !this.tabs.length) return;

      const progress = (this.activeIndex + 1) / this.tabs.length;
      this.progressFill.style.width = '100%';
      this.progressFill.style.transformOrigin = 'left center';
      this.progressFill.style.transform = `scaleX(${progress})`;
    }

    getRelativePosition(panelIndex, activeIndex) {
      let position = panelIndex - activeIndex;
      const half = this.panels.length / 2;

      if (position > half) position -= this.panels.length;
      if (position < -half) position += this.panels.length;
      return position;
    }

    handleTransitionEnd(event) {
      if (!(event.target instanceof Element) || event.propertyName !== 'transform') return;
      if (!event.target.classList.contains('sa-templates__panel')) return;
      this.panels.forEach((panel) => panel.classList.remove('is-moving'));
    }

    handlePointerDown(event) {
      if (this.panels.length < 2 || (event.pointerType === 'mouse' && event.button !== 0)) return;

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerDeltaX = 0;
      this.isDragging = false;
      this.panelsStage.setPointerCapture?.(event.pointerId);
    }

    handlePointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;
      if (!this.isDragging) {
        if (Math.abs(deltaX) < 6) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) return;
        this.isDragging = true;
        this.panelsStage.classList.add('is-dragging');
      }

      if (event.cancelable) event.preventDefault();
      const dragLimit = this.panelsStage.clientWidth * 0.45;
      this.pointerDeltaX = Math.max(-dragLimit, Math.min(dragLimit, deltaX));
      this.panelsStage.style.setProperty('--sa-templates-drag-offset', `${this.pointerDeltaX}px`);
    }

    handlePointerEnd(event) {
      if (event.pointerId !== this.pointerId) return;

      const wasDragging = this.isDragging;
      const deltaX = this.pointerDeltaX || 0;
      const threshold = Math.min(80, this.panelsStage.clientWidth * 0.12);
      this.releasePointer(event.pointerId);
      if (!wasDragging) return;

      this.panelsStage.getBoundingClientRect();
      this.panelsStage.classList.remove('is-dragging');
      this.panelsStage.style.setProperty('--sa-templates-drag-offset', '0px');

      if (Math.abs(deltaX) < threshold) return;
      const direction = deltaX < 0 ? 1 : -1;
      const nextIndex = (this.activeIndex + direction + this.panels.length) % this.panels.length;
      this.activate(nextIndex);
    }

    handlePointerCancel(event) {
      if (event.pointerId !== this.pointerId) return;
      this.releasePointer(event.pointerId);
      this.panelsStage.classList.remove('is-dragging');
      this.panelsStage.style.setProperty('--sa-templates-drag-offset', '0px');
    }

    releasePointer(pointerId) {
      if (this.panelsStage.hasPointerCapture?.(pointerId)) {
        this.panelsStage.releasePointerCapture(pointerId);
      }
      this.pointerId = null;
      this.pointerDeltaX = 0;
      this.isDragging = false;
    }

    scrollTabIntoView(tab) {
      const viewport = this.querySelector('.sa-templates__tabs-viewport');
      if (!viewport || viewport.scrollWidth <= viewport.clientWidth) return;

      const tabStart = tab.offsetLeft;
      const tabEnd = tabStart + tab.offsetWidth;
      const viewportStart = viewport.scrollLeft;
      const viewportEnd = viewportStart + viewport.clientWidth;
      let nextScrollLeft = viewportStart;

      if (tabStart < viewportStart) nextScrollLeft = tabStart;
      if (tabEnd > viewportEnd) nextScrollLeft = tabEnd - viewport.clientWidth;
      if (nextScrollLeft === viewportStart) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      viewport.scrollTo({
        left: nextScrollLeft,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
  }

  if (!customElements.get('sa-templates')) {
    customElements.define('sa-templates', SaTemplates);
  }
})();
